import { APP_TZ } from '@hadir/shared';

import type { Lang } from '@/stores/ui.store';

/**
 * Formatting helpers (05 §6.1).
 *
 * Western Arabic digits (0-9) everywhere, including the Arabic UI: university ids
 * and counters must match what students see on their phones and what lands in
 * Excel. That is what the `-u-nu-latn` extension on the Arabic locale buys.
 * Times are 24 h.
 *
 * ── Two clocks, and which one a screen may use ───────────────────────────
 * The `format*` family below renders in the **browser's** zone. That is right
 * for "when did this happen on the machine I am holding" — a toast timestamp,
 * a relative age — and wrong for anything that belongs to a lecture.
 *
 * Every `scheduledAt`, `openedAt`, `closedAt` and `scannedAt` is a moment in a
 * hall in Cairo, and must go through the `formatCairo*` family at the bottom of
 * this file. Not a style preference: the section form writes those values as
 * Cairo wall-clock, Egypt has observed DST again since 2023, and a laptop with
 * a stale or foreign zone otherwise shows one time in the course list and a
 * different time on the section page for the same lecture. Label the column or
 * the field with `sections.fields.tzNote` so the reader knows which clock they
 * are reading.
 */

const LOCALE: Record<Lang, string> = {
  ar: 'ar-EG-u-nu-latn',
  en: 'en-GB',
};

/** Renders in the browser's own zone — see the note above before using it. */
export function formatDate(value: string | number | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(LOCALE[lang], {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));
}

export function formatShortDate(value: string | number | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(LOCALE[lang], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

export function formatTime(value: string | number | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(LOCALE[lang], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

export function formatTimeWithSeconds(value: string | number | Date, lang: Lang): string {
  return new Intl.DateTimeFormat(LOCALE[lang], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

export function formatDateTime(value: string | number | Date, lang: Lang): string {
  return `${formatShortDate(value, lang)} · ${formatTime(value, lang)}`;
}

export function formatNumber(value: number, lang: Lang): string {
  return new Intl.NumberFormat(LOCALE[lang]).format(value);
}

export function formatPercent(value: number, lang: Lang, fractionDigits = 1): string {
  return new Intl.NumberFormat(LOCALE[lang], {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/* ───────────────────── the Cairo clock (lecture times) ─────────────────── */

/**
 * Every lecture time in the dashboard goes through these.
 *
 * `Intl` with an explicit `timeZone` rather than an offset arithmetic, because
 * "add two hours" is wrong for the four months a year Egypt is on DST. An
 * unparseable value renders as an em dash rather than `Invalid Date`, so a
 * malformed row degrades to a blank cell instead of shouting in English.
 */
function cairoFormat(
  value: string | number | Date,
  lang: Lang,
  options: Intl.DateTimeFormatOptions,
): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE[lang], { timeZone: APP_TZ, ...options }).format(instant);
}

/** `08/09/2026` in Cairo. */
export function formatCairoShortDate(value: string | number | Date, lang: Lang): string {
  return cairoFormat(value, lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** The long, spelled-out date in Cairo — a page heading, not a table cell. */
export function formatCairoDate(value: string | number | Date, lang: Lang): string {
  return cairoFormat(value, lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** `14:30` in Cairo — the short form, when the date is already on screen. */
export function formatCairoTime(value: string | number | Date, lang: Lang): string {
  return cairoFormat(value, lang, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** `14:30:05` in Cairo — the live feed, where seconds are the whole point. */
export function formatCairoTimeWithSeconds(value: string | number | Date, lang: Lang): string {
  return cairoFormat(value, lang, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * `08/09/2026 · 14:30` in Cairo — the default for a `scheduledAt`, `openedAt`,
 * `closedAt` or `scannedAt` anywhere in the dashboard.
 *
 * Composed with the same ` · ` separator as `formatDateTime` rather than left
 * to `Intl`'s own comma, so swapping a call site from the browser clock to the
 * Cairo one is a pure time-zone change and nothing on the screen moves.
 */
export function formatCairoDateTime(value: string | number | Date, lang: Lang): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '—';
  return `${formatCairoShortDate(instant, lang)} · ${formatCairoTime(instant, lang)}`;
}

/** The zone every `formatCairo*` result is in, for a screen that wants to say so. */
export { APP_TZ as DISPLAY_TZ };

/** `mm:ss` countdown for the phase badge and the close timer (05 §2.8). */
export function formatCountdown(ms: number): string {
  const clamped = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
